import { useEffect, useMemo, useState } from 'react';
import {
  adminLogin,
  createProject,
  deleteProject,
  getAllProjectsAdmin,
  updateProject,
} from '../services/projectService';

function Modal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4">
      <div
        className="absolute inset-0 bg-charcoal/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-3xl bg-cream-50 rounded-2xl shadow-card-hover border border-charcoal/10 p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-xl sm:text-2xl font-serif font-semibold text-charcoal">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-xl border border-charcoal/10 hover:bg-charcoal/5 text-charcoal/80"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Sidebar({ active, onLogout }) {
  return (
    <aside className="hidden md:flex md:w-72 lg:w-80 flex-col bg-cream-100 border-r border-gold/10">
      <div className="p-6 border-b border-gold/10">
        <div className="inline-flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gold/20 border border-gold/30 flex items-center justify-center text-charcoal font-serif font-bold text-2xl">
            A
          </div>
          <div>
            <p className="text-charcoal font-serif font-bold text-lg">Admin Panel</p>
            <p className="text-charcoal/50 text-xs uppercase tracking-widest-plus">
              Project Management
            </p>
          </div>
        </div>
      </div>

      <nav className="p-4 space-y-2">
        <button
          type="button"
          className={`w-full text-left px-4 py-3 rounded-2xl border transition-colors ${
            active === 'projects'
              ? 'bg-charcoal text-white border-charcoal'
              : 'bg-cream-50 border-charcoal/10 hover:bg-cream-50/80 text-charcoal'
          }`}
        >
          Projects
        </button>
      </nav>

      <div className="mt-auto p-4 border-t border-gold/10">
        <button
          type="button"
          onClick={onLogout}
          className="w-full px-4 py-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
        >
          Logout
        </button>
      </div>
    </aside>
  );
}

function ProjectFormModal({
  open,
  mode,
  initialProject,
  onClose,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  const [imageFile, setImageFile] = useState(null);
  const [form, setForm] = useState({
    name: '',
    location: '',
    description: '',
    longDescription: '',
    type: '',
    units: '',
    totalUnits: '',
    area: '',
    status: 'Ongoing',
    amenities: '',
    mapUrl: '',
  });

  useEffect(() => {
    if (!open) return;
    setImageFile(null);
    setForm({
      name: initialProject?.name || '',
      location: initialProject?.location || '',
      description: initialProject?.description || '',
      longDescription: initialProject?.longDescription || '',
      type: initialProject?.type || '',
      units: initialProject?.units || '',
      totalUnits:
        initialProject?.totalUnits !== undefined && initialProject?.totalUnits !== null
          ? String(initialProject.totalUnits)
          : '',
      area: initialProject?.area || '',
      status: initialProject?.status || 'Ongoing',
      amenities: Array.isArray(initialProject?.amenities)
        ? initialProject.amenities.join(', ')
        : initialProject?.amenities
          ? String(initialProject.amenities)
          : '',
      mapUrl: initialProject?.mapUrl || '',
    });
  }, [open, initialProject]);

  const galleryPreview = useMemo(() => {
    const image = initialProject?.image;
    const first = Array.isArray(initialProject?.gallery) ? initialProject.gallery[0] : null;
    return image || first || '';
  }, [initialProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmenitiesChange = (e) => {
    setForm((prev) => ({ ...prev, amenities: e.target.value }));
  };

  const handleSubmit = async () => {
    await onSubmit({
      imageFile,
      data: {
        ...form,
        totalUnits: form.totalUnits ? Number(form.totalUnits) : undefined,
        amenities: form.amenities
          ? form.amenities
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      },
    });
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Project' : 'Add Project'}
      onClose={onClose}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Project Image {isEdit ? <span className="text-charcoal/50">(optional)</span> : <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-start gap-4">
              <div className="w-32 h-24 rounded-xl overflow-hidden border border-charcoal/10 bg-white flex items-center justify-center">
                {galleryPreview ? (
                  // eslint-disable-next-line jsx-a11y/alt-text
                  <img src={galleryPreview} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-charcoal/40 text-xs">Preview</span>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., Rameshwar Dham"
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., Nepanagar Road, Burhanpur"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Short Description
            </label>
            <input
              name="description"
              value={form.description}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="Short overview shown on cards"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Long Description
            </label>
            <textarea
              name="longDescription"
              value={form.longDescription}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40 resize-none"
              placeholder="Detailed project description"
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <input
              name="type"
              value={form.type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., Residential Apartments"
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Units
            </label>
            <input
              name="units"
              value={form.units}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., 2 & 3 BHK"
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Total Units
            </label>
            <input
              type="number"
              name="totalUnits"
              value={form.totalUnits}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., 120"
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Area Range
            </label>
            <input
              name="area"
              value={form.area}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="e.g., 1050 - 1650 sq.ft."
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Status
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
            >
              <option value="Ongoing">Ongoing</option>
              <option value="Completed">Completed</option>
              <option value="Upcoming">Upcoming</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Amenities (comma separated)
            </label>
            <input
              name="amenities"
              value={form.amenities}
              onChange={handleAmenitiesChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="Swimming Pool, Gymnasium, 24/7 Security"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Map URL
            </label>
            <input
              name="mapUrl"
              value={form.mapUrl}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="Google maps embed URL"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-charcoal/10">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 rounded-xl border border-charcoal/10 hover:bg-charcoal/5 text-charcoal/80"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-3 rounded-xl bg-gold text-charcoal hover:bg-gold/90 transition-colors font-sans font-semibold"
          >
            {isEdit ? 'Save Changes' : 'Add Project'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function AdminPanel() {
  const [authed, setAuthed] = useState(() => Boolean(localStorage.getItem('admin_login')));

  const [login, setLogin] = useState({
    AdminUsername: '',
    AdminPassword: '',
  });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create | edit
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const data = await getAllProjectsAdmin();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogout = () => {
    localStorage.removeItem('admin_login');
    setAuthed(false);
  };

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedProject(null);
    setModalOpen(true);
  };

  const openEditModal = (project) => {
    setModalMode('edit');
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    const { AdminUsername, AdminPassword } = login;
    if (!AdminUsername || !AdminPassword) {
      setLoginError('Please enter username and password.');
      return;
    }

    setLoginLoading(true);
    try {
      const res = await adminLogin({ AdminUsername, AdminPassword });
      if (res?.data) {
        localStorage.setItem('admin_login', 'true');
        setAuthed(true);
      } else {
        setLoginError(res?.message || 'Invalid credentials.');
      }
    } catch (err) {
      setLoginError(err.message || 'Login failed.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleModalSubmit = async ({ imageFile, data }) => {
    setModalBusy(true);
    try {
      // Basic validation
      if (!data.name || !data.location || !data.type) {
        throw new Error('Name, Location and Type are required.');
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('location', data.location);
      formData.append('description', data.description || '');
      formData.append('longDescription', data.longDescription || '');
      formData.append('type', data.type);
      formData.append('units', data.units || '');
      if (data.totalUnits !== undefined) formData.append('totalUnits', String(data.totalUnits));
      formData.append('area', data.area || '');
      formData.append('status', data.status || 'Ongoing');
      formData.append('amenities', JSON.stringify(data.amenities || []));
      formData.append('mapUrl', data.mapUrl || '');

      // Only append image if provided (required on create, optional on edit).
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (modalMode === 'edit') {
        const id = selectedProject?.id;
        if (!id) throw new Error('Missing project id.');
        await updateProject(id, formData);
      } else {
        await createProject(formData);
      }

      setModalOpen(false);
      await fetchProjects();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert(err.message || 'Operation failed.');
    } finally {
      setModalBusy(false);
    }
  };

  const handleDelete = async (project) => {
    const id = project?.id;
    if (!id) return;
    const ok = window.confirm(`Delete project "${project.name}"? This cannot be undone.`);
    if (!ok) return;

    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => (p.id || p._id) !== id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert(err.message || 'Delete failed.');
    }
  };

  const authedView = (
    <div className="min-h-screen bg-cream-50 flex">
      <Sidebar active="projects" onLogout={handleLogout} />

      <section className="flex-1 p-4 sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-charcoal">
              Projects Management
            </h1>
            <p className="text-charcoal/50 text-sm mt-1">
              Add, edit and remove projects displayed on the website.
            </p>
          </div>

          <button type="button" onClick={openCreateModal} className="btn-premium-filled">
            <span>Add Project</span>
          </button>
        </div>

        <div className="bg-white border border-charcoal/10 rounded-2xl shadow-premium overflow-hidden">
          <div className="p-4 border-b border-charcoal/10 bg-cream-100/60">
            <p className="text-charcoal/80 font-sans font-medium text-sm">
              Total projects: {projects.length}
            </p>
          </div>

          {loadingProjects ? (
            <div className="p-10 text-center text-charcoal/40 text-sm">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full text-left">
                <thead className="bg-charcoal/95 text-white">
                  <tr>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">S.No</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Image</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Name</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Location</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Status</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Type</th>
                    <th className="px-4 py-3 text-sm font-sans font-semibold">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {projects.map((project, idx) => (
                    <tr key={project.id || project._id} className="border-b border-charcoal/10">
                      <td className="px-4 py-4 text-sm text-charcoal/60">{idx + 1}</td>
                      <td className="px-4 py-4">
                        {project.image || project.gallery?.[0] ? (
                          // eslint-disable-next-line jsx-a11y/alt-text
                          <img
                            src={project.image || project.gallery[0]}
                            className="w-16 h-12 object-cover rounded-xl border border-charcoal/10"
                          />
                        ) : (
                          <span className="text-charcoal/30 text-xs">No image</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-sans font-medium text-charcoal">
                        {project.name}
                      </td>
                      <td className="px-4 py-4 text-sm text-charcoal/60">{project.location}</td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-sans font-medium border ${
                            project.status === 'Completed'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : project.status === 'Ongoing'
                                ? 'bg-gold/20 text-gold border-gold/30'
                                : 'bg-white text-charcoal/70 border-charcoal/10'
                          }`}
                        >
                          {project.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-charcoal/60">{project.type}</td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(project)}
                            className="px-3 py-2 rounded-xl border border-charcoal/10 hover:bg-charcoal/5 text-charcoal/80 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(project)}
                            className="px-3 py-2 rounded-xl border border-red-200 hover:bg-red-50 text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {projects.length === 0 && !loadingProjects && (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-charcoal/40 text-sm">
                        No projects found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <ProjectFormModal
        open={modalOpen}
        mode={modalMode}
        initialProject={selectedProject}
        onClose={() => {
          if (modalBusy) return;
          setModalOpen(false);
        }}
        onSubmit={handleModalSubmit}
      />
    </div>
  );

  const loginView = (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-premium border border-charcoal/10 p-8">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-gold/20 border border-gold/30 flex items-center justify-center text-charcoal font-serif font-bold text-3xl">
            A
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-charcoal">Admin Login</h1>
            <p className="text-sm text-charcoal/50">Sign in to manage projects.</p>
          </div>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Username
            </label>
            <input
              value={login.AdminUsername}
              onChange={(e) => setLogin((prev) => ({ ...prev, AdminUsername: e.target.value }))}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-cream-50 outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="Enter username"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-sans font-medium text-charcoal/80 mb-2">
              Password
            </label>
            <input
              type="password"
              value={login.AdminPassword}
              onChange={(e) => setLogin((prev) => ({ ...prev, AdminPassword: e.target.value }))}
              className="w-full px-4 py-3 border border-charcoal/10 rounded-xl bg-cream-50 outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="Enter password"
            />
          </div>

          {loginError ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {loginError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={loginLoading}
            className="btn-premium-filled w-full disabled:opacity-60"
          >
            <span>{loginLoading ? 'Logging in...' : 'Login'}</span>
          </button>
        </form>
      </div>
    </div>
  );

  return authed ? authedView : loginView;
}

